import { Suspense, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import Layout from './components/Layout'
import Login from './views/auth/Login'
import VerifyEmail from './views/auth/VerifyEmail'
import ResendVerification from './views/auth/ResendVerification'
import ForgotPassword from './views/auth/ForgotPassword'
import ResetPassword from './views/auth/ResetPassword'
import Verify2FA from './views/auth/Verify2FA'
import UserClasses from './views/user/UserClasses'
import Class from './views/user/Class'
import Students from './views/manage/Students'
import StudentGeneralPage from './views/manage/student/StudentGeneralPage.tsx'
import StudentAccommodationsPage from './views/manage/student/StudentAccommodationsPage.tsx'
import StudentGoalsPage from './views/manage/student/StudentGoalsPage.tsx'
import GoalForm from './views/manage/student/GoalForm.tsx'
import Schools from './views/manage/Schools'
import SchoolEdit from './views/manage/SchoolEdit'
import Classes from './views/manage/Classes'
import ClassEdit from './views/manage/ClassEdit'
import Users from './views/manage/Users'
import UserEdit from './views/manage/UserEdit'
import Import from './views/manage/import'
import RequestDemo from './views/marketing/RequestDemo'
import About from './views/marketing/About'
import Pricing from './views/marketing/Pricing'
import TermsOfService from './views/marketing/TermsOfService'
import DataProcessingAddendum from './views/marketing/DataProcessingAddendum'
import PrivacyPolicy from './views/marketing/PrivacyPolicy'
import Videos from './views/marketing/Videos'
import RawEventLogs from './views/accommodations/RawEventLogs'
const AccommodationUsage = lazy(
  () => import('./views/accommodations/AccommodationUsage'),
)
import RawGoalEventLogs from './views/goals/RawGoalEventLogs'
import StudentsList from './views/goals/StudentsList'
import StudentGoals from './views/goals/StudentGoals'
import GoalEventsList from './views/goals/GoalEventsList'
import GoalEventForm from './views/goals/GoalEventForm'
import ProtectedRoute from './components/ProtectedRoute'
import PermissionGuard from './components/PermissionGuard'
import ConditionalRedirect from './components/ConditionalRedirect'
import { Permission } from './types'
import ScrollToTop from './components/ScrollToTop'
import UnauthedLayout from './components/UnauthedLayout.tsx'

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <div className="min-h-screen bg-gray-50">
          <Routes>
            {/* Unprotected Routes */}
            <Route element={<UnauthedLayout />}>
              <Route path="/login" element={<Login />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route
                path="/resend-verification"
                element={<ResendVerification />}
              />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-2fa" element={<Verify2FA />} />
              <Route path="/" element={<ConditionalRedirect />} />
              <Route path="/request-demo" element={<RequestDemo />} />
              <Route path="/about" element={<About />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/videos" element={<Videos />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route
                path="/data-processing-addendum"
                element={<DataProcessingAddendum />}
              />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            </Route>

            {/* Protected Routes */}
            <Route
              path="/user/classes"
              element={
                <ProtectedRoute>
                  <Layout>
                    <UserClasses />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/user/class/:classId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Class />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage/schools"
              element={
                <ProtectedRoute>
                  <PermissionGuard
                    requiredPermission={Permission.MANAGE_SCHOOLS}
                  >
                    <Layout>
                      <Schools />
                    </Layout>
                  </PermissionGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage/schools/:id"
              element={
                <ProtectedRoute>
                  <PermissionGuard
                    requiredPermission={Permission.MANAGE_SCHOOLS}
                  >
                    <Layout>
                      <SchoolEdit />
                    </Layout>
                  </PermissionGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage/classes"
              element={
                <ProtectedRoute>
                  <PermissionGuard
                    requiredPermission={Permission.MANAGE_CLASSES}
                  >
                    <Layout>
                      <Classes />
                    </Layout>
                  </PermissionGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage/classes/:id"
              element={
                <ProtectedRoute>
                  <PermissionGuard
                    requiredPermission={Permission.MANAGE_CLASSES}
                  >
                    <Layout>
                      <ClassEdit />
                    </Layout>
                  </PermissionGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage/users"
              element={
                <ProtectedRoute>
                  <PermissionGuard requiredPermission={Permission.MANAGE_USERS}>
                    <Layout>
                      <Users />
                    </Layout>
                  </PermissionGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage/users/:id"
              element={
                <ProtectedRoute>
                  <PermissionGuard requiredPermission={Permission.MANAGE_USERS}>
                    <Layout>
                      <UserEdit />
                    </Layout>
                  </PermissionGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage/students"
              element={
                <ProtectedRoute>
                  <PermissionGuard
                    requiredPermission={Permission.MANAGE_STUDENTS}
                  >
                    <Layout>
                      <Students />
                    </Layout>
                  </PermissionGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage/students/:studentId/general"
              element={
                <ProtectedRoute>
                  <PermissionGuard
                    requiredPermission={Permission.MANAGE_STUDENTS}
                  >
                    <Layout>
                      <StudentGeneralPage />
                    </Layout>
                  </PermissionGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage/students/:studentId/accommodations"
              element={
                <ProtectedRoute>
                  <PermissionGuard
                    requiredPermission={Permission.MANAGE_STUDENTS}
                  >
                    <Layout>
                      <StudentAccommodationsPage />
                    </Layout>
                  </PermissionGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage/students/:studentId/goals/new"
              element={
                <ProtectedRoute>
                  <PermissionGuard
                    requiredPermission={Permission.MANAGE_STUDENTS}
                  >
                    <Layout>
                      <GoalForm />
                    </Layout>
                  </PermissionGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage/students/:studentId/goals/:goalId"
              element={
                <ProtectedRoute>
                  <PermissionGuard
                    requiredPermission={Permission.MANAGE_STUDENTS}
                  >
                    <Layout>
                      <GoalForm />
                    </Layout>
                  </PermissionGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage/students/:studentId/goals"
              element={
                <ProtectedRoute>
                  <PermissionGuard
                    requiredPermission={Permission.MANAGE_STUDENTS}
                  >
                    <Layout>
                      <StudentGoalsPage />
                    </Layout>
                  </PermissionGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage/students/:id"
              element={
                <ProtectedRoute>
                  <PermissionGuard
                    requiredPermission={Permission.MANAGE_STUDENTS}
                  >
                    <Layout>
                      <StudentGeneralPage />
                    </Layout>
                  </PermissionGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/manage/import"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Import />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accommodations/raw-event-logs"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RawEventLogs />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/accommodations/usage"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Suspense fallback={<div>Loading charts...</div>}>
                      <AccommodationUsage />
                    </Suspense>
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/goals/students"
              element={
                <ProtectedRoute>
                  <Layout>
                    <StudentsList />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/goals/students/:studentId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <StudentGoals />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/goals/:goalId/goal-events"
              element={
                <ProtectedRoute>
                  <Layout>
                    <GoalEventsList />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/goals/:goalId/goal-events/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <GoalEventForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/goals/:goalId/goal-events/:goalEventId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <GoalEventForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/goals/raw-goal-event-logs"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RawGoalEventLogs />
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  )
}

export default App
